import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Plus, Minus, Edit3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const REASONS = [
  { value: "gift",         label: "Regalo",                          hint: "Puntos regalo (nuevo registro, fidelidad, bienvenida…)" },
  { value: "compensation", label: "Compensación por queja",          hint: "Compensar una mala experiencia del cliente" },
  { value: "correction",   label: "Corrección de error",             hint: "Arreglar un apunte erróneo previo" },
  { value: "bonus",        label: "Bonus / promoción",               hint: "Promoción especial, doble puntos, etc." },
  { value: "other",        label: "Otro motivo",                     hint: "Cualquier otro ajuste manual" },
];

/**
 * Dialog para sumar o restar puntos manualmente, obligando a indicar un motivo.
 * Crea una entry en loyalty_transactions con type='adjust' + source del motivo.
 *
 * Props: open, onClose, customer, onSuccess
 */
export default function AdjustPointsDialog({ open, onClose, customer, onSuccess }) {
  const [sign, setSign] = useState("+");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("gift");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setSign("+"); setPoints(""); setReason("gift"); setNotes(""); };

  const submit = async () => {
    const n = parseInt(points, 10);
    if (!n || n <= 0) return toast.error("Indica una cantidad válida");
    const delta = sign === "+" ? n : -n;
    const current = customer?.points || 0;
    if (delta < 0 && Math.abs(delta) > current) {
      return toast.error(`No puedes restar más de ${current} pts (saldo actual del cliente)`);
    }
    setSaving(true);
    try {
      const r = await api.adjustCustomerPoints(customer.customer_id, {
        points_delta: delta,
        reason,
        notes: notes.trim() || null,
      });
      toast.success(`${sign === "+" ? "+" : "-"}${n} pts · Nuevo saldo: ${r.new_total}`);
      onSuccess?.(r);
      reset();
      onClose();
    } catch (err) {
      toast.error(err.message || "No se pudo ajustar");
    } finally {
      setSaving(false);
    }
  };

  const selected = REASONS.find(r => r.value === reason);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && (reset(), onClose())}>
      <DialogContent className="max-w-md" data-testid="adjust-points-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5" /> Ajustar puntos — {customer?.name}
          </DialogTitle>
          <DialogDescription>
            Suma o resta puntos manualmente. Queda registrado en el historial con el motivo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Sign + amount */}
          <div>
            <Label>Operación</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant={sign === "+" ? "default" : "outline"}
                onClick={() => setSign("+")}
                className={sign === "+" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                data-testid="adjust-sign-plus"
              >
                <Plus className="w-4 h-4 mr-1" /> Sumar
              </Button>
              <Button
                type="button"
                variant={sign === "-" ? "default" : "outline"}
                onClick={() => setSign("-")}
                className={sign === "-" ? "bg-rose-600 hover:bg-rose-700" : ""}
                data-testid="adjust-sign-minus"
              >
                <Minus className="w-4 h-4 mr-1" /> Restar
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="adjust-points-amount">Cantidad de puntos</Label>
            <Input
              id="adjust-points-amount"
              type="number"
              inputMode="numeric"
              min="1"
              placeholder="Ej. 50"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              data-testid="adjust-points-amount"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              Saldo actual: {customer?.points || 0} pts
            </p>
          </div>

          <div>
            <Label>Motivo (obligatorio)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="adjust-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`adjust-reason-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected?.hint && (
              <p className="text-xs text-muted-foreground mt-1">{selected.hint}</p>
            )}
          </div>

          <div>
            <Label htmlFor="adjust-notes">Nota interna (opcional)</Label>
            <Textarea
              id="adjust-notes"
              rows={2}
              placeholder="Contexto visible solo para el equipo…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="adjust-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !points} data-testid="adjust-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Confirmar ajuste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
